import { z } from "zod";

export const VehicleImagePositions = [
  "topImage",
  "topLeftImage",
  "topRightImage",
  "bottomLeftImage",
  "bottomRightImage",
  "bottomImage",
] as const;

export const VehicleExtraImagePositions = ["dashboard", "cables", "cles"] as const;
export const VehicleImagePhases = ["Before", "After"] as const;

export type VehicleImagePosition = (typeof VehicleImagePositions)[number];
export type VehicleExtraImagePosition = (typeof VehicleExtraImagePositions)[number];
export type VehicleImagePhase = (typeof VehicleImagePhases)[number];

export const VehicleImageLocationSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  fullAddress: z.string().nullable().optional(),
});

export const VehicleImageMetadataSchema = z.object({
  takenAt: z.string().nullable(),
  location: VehicleImageLocationSchema.nullable(),
  format: z.string().optional(),
});

export const VehicleImageSchema = VehicleImageMetadataSchema.extend({
  key: z.string().optional(),
  url: z.string(),
});

export const VehicleImagesSchema = z.object({
  topImage: VehicleImageSchema.optional(),
  topLeftImage: VehicleImageSchema.optional(),
  topRightImage: VehicleImageSchema.optional(),
  bottomLeftImage: VehicleImageSchema.optional(),
  bottomRightImage: VehicleImageSchema.optional(),
  bottomImage: VehicleImageSchema.optional(),
  extraImages: z.record(z.string(), VehicleImageSchema).optional(),
});

export type VehicleImageLocation = z.infer<typeof VehicleImageLocationSchema>;
export type VehicleImageMetadata = z.infer<typeof VehicleImageMetadataSchema>;
export type VehicleImage = z.infer<typeof VehicleImageSchema>;
export type VehicleImages = z.infer<typeof VehicleImagesSchema>;

export function buildVehicleImageUploadPath(
  deliveryId: string | number,
  vehicleId: string | number,
  phase: VehicleImagePhase,
  position: VehicleImagePosition | VehicleExtraImagePosition
): string {
  const extraFolder = (VehicleExtraImagePositions as readonly string[]).includes(position)
    ? "/extra_images"
    : "";
  return `delivery/${deliveryId}/car-${vehicleId}/${phase}${extraFolder}/${position}`;
}

export function getVehicleImageStorageKey(value: Pick<VehicleImage, "key" | "url">): string | null {
  if (value.key) return value.key.replace(/^\/+/, "");
  if (!value.url) return null;

  const protocolIndex = value.url.indexOf("://");
  if (protocolIndex === -1) return value.url.replace(/^\/+/, "");

  const pathIndex = value.url.indexOf("/", protocolIndex + 3);
  return pathIndex === -1 ? null : value.url.slice(pathIndex + 1);
}

export function buildCompressedVehicleImageKey(originalKey: string): string | null {
  const key = originalKey.replace(/^\/+/, "").split(/[?#]/, 1)[0];
  const match = key.match(
    /^delivery\/[^/]+\/car-[^/]+\/(Before|After)\/(?:(extra_images)\/)?([^/.]+)\.[^/]+$/
  );
  if (!match) return null;

  const [, , extraFolder, position] = match;
  const allowedPositions = extraFolder ? VehicleExtraImagePositions : VehicleImagePositions;
  if (!(allowedPositions as readonly string[]).includes(position)) return null;

  return `${key.slice(0, key.lastIndexOf("."))}/full.jpeg`;
}
